import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  createFeeStructure,
  getAllFeeStructures,
  controlPaymentAcceptance,
  getAllBatchesViaAdmin,
  updateFeeStructure,
  getSingleProgramViaAdmin,
  bulkUpdateFeeStructures,
} from "../../../../utils/Api";
import {
  buildAcademicYears,
  getCurrentAcademicYear,
} from "../../../../utils/academicYears";
import {
  getTuitionFeeDisplay,
  isTuitionConcessionEligible,
} from "../../../../utils/categoryFee";
import { formatCurrency, formatDate, LoadingSpinner } from "../components/feesUtils";

// Fee Structures tab: create/edit/toggle CRUD + search/year/status filters, with
// the create/edit form living in a right slide-over drawer and multi-select bulk
// actions for applying a common change across many structures at once.

// The five caste categories the program concession config supports (mirrors
// ProgramForm.jsx's categoryConcessionInputs).
const CASTE_CATEGORIES = [
  { key: "sc", label: "SC" },
  { key: "st", label: "ST" },
  { key: "obc", label: "OBC" },
  { key: "general", label: "General" },
  { key: "other", label: "Other" },
];

// Factory (not a shared constant) so each reset/open gets a fresh object with its
// own installments array — reusing one literal would let edits leak between opens.
const buildInitialFormData = () => ({
  batchId: "",
  batchName: "",
  academicYear: getCurrentAcademicYear(),
  fees: {
    tuitionFee: 0,
    developmentFee: 0,
  },
  paymentStructure: {
    allowInstallments: false,
    installments: [
      { installmentNumber: 1, amount: 0, dueDate: "", description: "First Installment" },
      { installmentNumber: 2, amount: 0, dueDate: "", description: "Second Installment" },
    ],
  },
  paymentAcceptance: {
    startDate: "",
    endDate: "",
    isAcceptingPayments: true,
    latePaymentAllowed: false,
    latePaymentPenalty: 0,
  },
});

const FeeStructuresTab = () => {
  const [feeStructures, setFeeStructures] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Caste-preview state: the program payload + year stage for the selected batch.
  // yearStage gates the tuition concession (FY is charged in full — see
  // CasteConcessionPreview), so it is tracked alongside the program config.
  const [previewProgram, setPreviewProgram] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewYearStage, setPreviewYearStage] = useState(null);

  // Bulk selection + action state.
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeBulkAction, setActiveBulkAction] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkWindow, setBulkWindow] = useState({ startDate: "", endDate: "" });
  const [bulkLate, setBulkLate] = useState({ latePaymentAllowed: false, latePaymentPenalty: 0 });

  const academicYears = buildAcademicYears();
  const currentAcademicYear = getCurrentAcademicYear();
  const [bulkYear, setBulkYear] = useState(currentAcademicYear);

  const [formData, setFormData] = useState(buildInitialFormData);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [structuresRes, batchesRes] = await Promise.all([
        getAllFeeStructures(),
        getAllBatchesViaAdmin(),
      ]);
      setFeeStructures(structuresRes.data || []);
      setBatches(batchesRes.batches || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Load the program concession config for a batch so the caste preview can
  // render. The batch payload carries { program: { id, name } } and yearStage.
  const loadPreviewProgram = async (batch) => {
    setPreviewYearStage(batch?.yearStage || null);
    const programId = batch?.program?.id;
    if (!programId) {
      setPreviewProgram(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getSingleProgramViaAdmin({ id: programId });
      setPreviewProgram(res?.program || null);
    } catch (error) {
      console.error("Error loading program concessions:", error);
      setPreviewProgram(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setFormData(buildInitialFormData());
    setShowForm(false);
    setEditMode(false);
    setEditingId(null);
    setPreviewProgram(null);
    setPreviewYearStage(null);
  }, []);

  // Close the drawer on ESC while it is open.
  useEffect(() => {
    if (!showForm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") resetForm();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showForm, resetForm]);

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [section, field] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
      }));
    }
  };

  const handleInstallmentChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      paymentStructure: {
        ...prev.paymentStructure,
        installments: prev.paymentStructure.installments.map((inst, i) =>
          i === index ? { ...inst, [field]: field === "amount" ? Number(value) : value } : inst
        ),
      },
    }));
  };

  const calculateTotalAmount = () => {
    return Object.values(formData.fees).reduce((sum, fee) => sum + Number(fee), 0);
  };

  const handleBatchChange = (e) => {
    const selectedBatch = batches.find((batch) => batch._id === e.target.value);
    setFormData((prev) => ({
      ...prev,
      batchId: e.target.value,
      batchName: selectedBatch ? selectedBatch.batchName : "",
    }));
    loadPreviewProgram(selectedBatch);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const totalAmount = calculateTotalAmount();
      let payload = { ...formData };

      if (formData.paymentStructure.allowInstallments) {
        payload = {
          ...payload,
          paymentStructure: {
            ...payload.paymentStructure,
            installments: payload.paymentStructure.installments.map((inst) => ({
              ...inst,
              amount: inst.amount || totalAmount / 2,
            })),
          },
        };
      }

      if (editMode && editingId) {
        await updateFeeStructure(editingId, payload);
        toast.success("Fee structure updated successfully");
      } else {
        await createFeeStructure(payload);
        toast.success("Fee structure created successfully");
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving fee structure:", error);
      toast.error(error.response?.data?.message || "Failed to save fee structure");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (structure) => {
    setEditMode(true);
    setEditingId(structure._id);
    setShowForm(true);
    const batchId = structure.batch?.id?._id || structure.batch?.id || "";
    setFormData({
      batchId,
      batchName: structure.batch?.name || "",
      academicYear: structure.academicYear,
      fees: { ...structure.fees },
      paymentStructure: {
        allowInstallments: structure.paymentStructure?.allowInstallments || false,
        installments: structure.paymentStructure?.installments?.length
          ? structure.paymentStructure.installments.map((inst) => ({
              ...inst,
              dueDate: inst.dueDate ? inst.dueDate.slice(0, 10) : "",
            }))
          : buildInitialFormData().paymentStructure.installments,
      },
      paymentAcceptance: {
        startDate: structure.paymentAcceptance?.startDate?.slice(0, 10) || "",
        endDate: structure.paymentAcceptance?.endDate?.slice(0, 10) || "",
        isAcceptingPayments: structure.paymentAcceptance?.isAcceptingPayments ?? true,
        latePaymentAllowed: structure.paymentAcceptance?.latePaymentAllowed ?? false,
        latePaymentPenalty: structure.paymentAcceptance?.latePaymentPenalty ?? 0,
      },
    });
    // Load the caste preview for the batch being edited.
    const batch = batches.find((b) => b._id === batchId);
    loadPreviewProgram(batch);
  };

  const togglePaymentAcceptance = async (id, currentStatus) => {
    try {
      await controlPaymentAcceptance(id, { isAcceptingPayments: !currentStatus });
      toast.success("Payment status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const filteredStructures = feeStructures.filter((s) => {
    const matchesSearch =
      !searchTerm ||
      (s.batch?.name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesYear = !filterYear || s.academicYear === filterYear;
    const matchesStatus =
      !filterStatus ||
      (filterStatus === "active" && s.paymentAcceptance?.isAcceptingPayments) ||
      (filterStatus === "inactive" && !s.paymentAcceptance?.isAcceptingPayments);
    return matchesSearch && matchesYear && matchesStatus;
  });

  const filteredIds = filteredStructures.map((s) => s._id);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));
  const someFilteredSelected = filteredIds.some((id) => selectedIds.includes(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setActiveBulkAction(null);
  };

  // Bulk selection can span filters, so this always applies to selectedIds
  // regardless of what is currently visible.
  const applyBulk = async (set) => {
    setBulkSaving(true);
    try {
      const res = await bulkUpdateFeeStructures(selectedIds, set);
      toast.success(res.message);
      if (res.skippedCount > 0) {
        const names = (res.skipped || []).map((s) => s.batchName).join(", ");
        toast.warn(`Skipped ${res.skippedCount} (academic year clash): ${names}`);
      }
      setActiveBulkAction(null);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const toggleBulkAction = (action) => {
    setActiveBulkAction((prev) => (prev === action ? null : action));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Fee Structures</h2>
          <p className="text-gray-600 mt-1">
            Manage fee configurations for different batches
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
        >
          Create Fee Structure
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Search by batch name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Academic Years</option>
            {academicYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Select-all + count */}
      {filteredStructures.length > 0 && (
        <div className="flex items-center justify-between mb-3 px-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              ref={(el) => {
                if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
              }}
              onChange={toggleSelectAllFiltered}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            Select all (filtered)
          </label>
          <span className="text-sm text-gray-500">
            {filteredStructures.length} structure{filteredStructures.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Fee Structures List */}
      {filteredStructures.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <i className="mdi mdi-file-document-outline text-gray-400 text-3xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No fee structures found</h3>
          <p className="text-gray-500">
            {searchTerm || filterYear || filterStatus
              ? "Try adjusting your filters"
              : "Create your first fee structure to get started"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredStructures.map((structure) => {
            const selected = selectedIds.includes(structure._id);
            return (
              <div
                key={structure._id}
                className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${
                  selected ? "ring-2 ring-blue-500" : ""
                }`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelect(structure._id)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {structure.batch?.name || "Unknown Batch"}
                        </h3>
                        <p className="text-sm text-gray-500">{structure.academicYear}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        structure.paymentAcceptance?.isAcceptingPayments
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {structure.paymentAcceptance?.isAcceptingPayments ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Total Fee</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatCurrency(structure.totalAmount)}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Payment Period</p>
                      <p className="text-sm text-gray-700">
                        {formatDate(structure.paymentAcceptance?.startDate)} -{" "}
                        {formatDate(structure.paymentAcceptance?.endDate)}
                      </p>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 mb-4">
                    <div className="flex justify-between py-1">
                      <span>Tuition Fee:</span>
                      <span>{formatCurrency(structure.fees?.tuitionFee || 0)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Development Fee:</span>
                      <span>{formatCurrency(structure.fees?.developmentFee || 0)}</span>
                    </div>
                    {structure.paymentStructure?.allowInstallments && (
                      <div className="flex items-center gap-2 py-1 text-blue-600">
                        <i className="mdi mdi-credit-card-check text-sm"></i>
                        <span>Installments enabled</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleEdit(structure)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        togglePaymentAcceptance(
                          structure._id,
                          structure.paymentAcceptance?.isAcceptingPayments
                        )
                      }
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        structure.paymentAcceptance?.isAcceptingPayments
                          ? "text-red-600 bg-red-50 hover:bg-red-100"
                          : "text-green-600 bg-green-50 hover:bg-green-100"
                      }`}
                    >
                      {structure.paymentAcceptance?.isAcceptingPayments
                        ? "Deactivate"
                        : "Activate"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-3xl">
          <div className="relative bg-white border border-gray-200 rounded-lg shadow-xl px-4 py-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {selectedIds.length} selected
            </span>
            <span className="h-5 w-px bg-gray-200 mx-1" />

            <BulkBarButton
              active={activeBulkAction === "status"}
              onClick={() => toggleBulkAction("status")}
            >
              Payment status
            </BulkBarButton>
            <BulkBarButton
              active={activeBulkAction === "window"}
              onClick={() => toggleBulkAction("window")}
            >
              Payment window
            </BulkBarButton>
            <BulkBarButton
              active={activeBulkAction === "late"}
              onClick={() => toggleBulkAction("late")}
            >
              Late payment
            </BulkBarButton>
            <BulkBarButton
              active={activeBulkAction === "year"}
              onClick={() => toggleBulkAction("year")}
            >
              Academic year
            </BulkBarButton>

            <button
              onClick={clearSelection}
              className="ml-auto px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear selection
            </button>

            {/* Popovers open upward from the bar */}
            {activeBulkAction === "status" && (
              <BulkPopover title="Set payment status">
                <div className="flex gap-2">
                  <button
                    disabled={bulkSaving}
                    onClick={() => applyBulk({ isAcceptingPayments: true })}
                    className="flex-1 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    Activate
                  </button>
                  <button
                    disabled={bulkSaving}
                    onClick={() => applyBulk({ isAcceptingPayments: false })}
                    className="flex-1 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    Deactivate
                  </button>
                </div>
              </BulkPopover>
            )}

            {activeBulkAction === "window" && (
              <BulkPopover title="Set payment window">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={bulkWindow.startDate}
                      onChange={(e) =>
                        setBulkWindow((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={bulkWindow.endDate}
                      onChange={(e) =>
                        setBulkWindow((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  disabled={bulkSaving || !bulkWindow.startDate || !bulkWindow.endDate}
                  onClick={() =>
                    applyBulk({
                      startDate: bulkWindow.startDate,
                      endDate: bulkWindow.endDate,
                    })
                  }
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {bulkSaving ? "Applying..." : "Apply to selected"}
                </button>
              </BulkPopover>
            )}

            {activeBulkAction === "late" && (
              <BulkPopover title="Late payment settings">
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkLate.latePaymentAllowed}
                    onChange={(e) =>
                      setBulkLate((prev) => ({ ...prev, latePaymentAllowed: e.target.checked }))
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Allow late payments</span>
                </label>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Late Payment Penalty
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      ₹
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={bulkLate.latePaymentPenalty}
                      onChange={(e) =>
                        setBulkLate((prev) => ({
                          ...prev,
                          latePaymentPenalty: Number(e.target.value),
                        }))
                      }
                      className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  disabled={bulkSaving}
                  onClick={() =>
                    applyBulk({
                      latePaymentAllowed: bulkLate.latePaymentAllowed,
                      latePaymentPenalty: bulkLate.latePaymentPenalty,
                    })
                  }
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {bulkSaving ? "Applying..." : "Apply to selected"}
                </button>
              </BulkPopover>
            )}

            {activeBulkAction === "year" && (
              <BulkPopover title="Set academic year">
                <select
                  value={bulkYear}
                  onChange={(e) => setBulkYear(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 mb-3"
                >
                  {academicYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <button
                  disabled={bulkSaving}
                  onClick={() => applyBulk({ academicYear: bulkYear })}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {bulkSaving ? "Applying..." : "Apply to selected"}
                </button>
              </BulkPopover>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit drawer (kept mounted so it slides in/out) */}
      <div
        className={`fixed inset-0 z-50 ${showForm ? "" : "pointer-events-none"}`}
        aria-hidden={!showForm}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            showForm ? "opacity-100" : "opacity-0"
          }`}
          onClick={resetForm}
        />
        <div
          role="dialog"
          aria-modal="true"
          className={`absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-y-auto transition-transform duration-300 ${
            showForm ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="sticky top-0 z-10 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800">
              {editMode ? "Edit Fee Structure" : "Create New Fee Structure"}
            </h3>
            <button
              onClick={resetForm}
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <i className="mdi mdi-close text-xl"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-8">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Basic Information
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.batchId}
                      onChange={handleBatchChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Batch</option>
                      {batches.map((batch) => (
                        <option key={batch._id} value={batch._id}>
                          {batch.batchName} ({batch.department?.departmentName || batch.department?.name})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Academic Year <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="academicYear"
                      value={formData.academicYear}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {academicYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Fee Breakdown
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tuition Fee
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        ₹
                      </span>
                      <input
                        type="number"
                        name="fees.tuitionFee"
                        value={formData.fees.tuitionFee}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Development Fee
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        ₹
                      </span>
                      <input
                        type="number"
                        name="fees.developmentFee"
                        value={formData.fees.developmentFee}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Total Amount</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatCurrency(calculateTotalAmount())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Caste-adjusted preview (read-only), driven by the fees entered above */}
              <CasteConcessionPreview
                program={previewProgram}
                loading={previewLoading}
                hasBatch={!!formData.batchId}
                yearStage={previewYearStage}
                tuitionBase={calculateTotalAmount()}
              />

              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Payment Period
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="paymentAcceptance.startDate"
                      value={formData.paymentAcceptance.startDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="paymentAcceptance.endDate"
                      value={formData.paymentAcceptance.endDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Payment Options
                </h4>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="paymentStructure.allowInstallments"
                      checked={formData.paymentStructure.allowInstallments}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Allow installment payments</span>
                  </label>

                  {formData.paymentStructure.allowInstallments && (
                    <div className="ml-7 space-y-3">
                      {formData.paymentStructure.installments.map((inst, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            Installment {inst.installmentNumber}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                ₹
                              </span>
                              <input
                                type="number"
                                value={inst.amount}
                                onChange={(e) =>
                                  handleInstallmentChange(idx, "amount", e.target.value)
                                }
                                placeholder="Amount"
                                className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <input
                              type="date"
                              value={inst.dueDate}
                              onChange={(e) =>
                                handleInstallmentChange(idx, "dueDate", e.target.value)
                              }
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="paymentAcceptance.latePaymentAllowed"
                      checked={formData.paymentAcceptance.latePaymentAllowed}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Allow late payments</span>
                  </label>

                  {formData.paymentAcceptance.latePaymentAllowed && (
                    <div className="ml-7">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Late Payment Penalty
                      </label>
                      <div className="relative w-48">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          ₹
                        </span>
                        <input
                          type="number"
                          name="paymentAcceptance.latePaymentPenalty"
                          value={formData.paymentAcceptance.latePaymentPenalty}
                          onChange={handleInputChange}
                          min="0"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editMode ? "Update" : "Create"} Fee Structure
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Bulk-bar action trigger. Highlighted while its popover is open.
const BulkBarButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
      active
        ? "bg-blue-600 text-white hover:bg-blue-700"
        : "text-gray-700 bg-gray-100 hover:bg-gray-200"
    }`}
  >
    {children}
  </button>
);

// Mini-form container that floats above the bulk bar.
const BulkPopover = ({ title, children }) => (
  <div className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-4">
    <h4 className="text-sm font-semibold text-gray-800 mb-3">{title}</h4>
    {children}
  </div>
);

// Shell so every preview state shares one heading.
const PreviewShell = ({ tone = "neutral", children }) => {
  const tones = {
    neutral: "bg-gray-50 border-gray-200",
    active: "bg-amber-50 border-amber-200",
    warn: "bg-orange-50 border-orange-200",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`}>
      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
        Caste-Adjusted Fee Preview
      </h4>
      {children}
    </div>
  );
};

// Read-only preview of the per-category concession on THIS fee structure's tuition.
//
// Bases off the tuition + development fee being entered in the form (live as typed) —
// NOT program.applicationFee, which is the separate FY admission fee and was what this
// preview used to show. A fee structure's amounts are the SY/TY/... money; the FY money
// is the admission fee, collected at application time.
//
// Only renders real numbers from SY onward: an FY student's concession is already
// applied to their admission fee, so discounting FY tuition would grant it twice. A
// batch with no structured yearStage is treated as ineligible (full fee) rather than
// risking a silent double concession — matching the backend gate.
const CasteConcessionPreview = ({
  program,
  loading,
  hasBatch,
  yearStage,
  tuitionBase,
}) => {
  if (!hasBatch) {
    return (
      <PreviewShell>
        <p className="text-sm text-gray-500">
          Select a batch to preview per-category tuition concessions.
        </p>
      </PreviewShell>
    );
  }

  if (loading) {
    return (
      <PreviewShell>
        <p className="text-sm text-gray-500">Loading program concessions…</p>
      </PreviewShell>
    );
  }

  if (!program) {
    return (
      <PreviewShell>
        <p className="text-sm text-gray-500">
          No program concession config found for this batch.
        </p>
      </PreviewShell>
    );
  }

  if (!yearStage) {
    return (
      <PreviewShell tone="warn">
        <p className="text-sm text-gray-600">
          This batch has no year stage set, so the category concession{" "}
          <strong>cannot be applied</strong> — every student will be charged the full
          amount. Set the year stage on the batch to enable concessions.
        </p>
      </PreviewShell>
    );
  }

  if (!isTuitionConcessionEligible(yearStage)) {
    return (
      <PreviewShell>
        <p className="text-sm text-gray-600">
          Not applicable at <strong>{yearStage}</strong>. First-year students receive
          their category concession on the admission fee, so first-year tuition is
          charged in full. Concessions apply from SY onward.
        </p>
      </PreviewShell>
    );
  }

  return (
    <PreviewShell tone="active">
      <p className="text-xs text-gray-500 mb-3">
        Based on this structure's tuition + development fee (
        {formatCurrency(tuitionBase)}) at <strong>{yearStage}</strong>. Read-only —
        applied automatically at fee checkout.
      </p>
      <div className="space-y-1.5">
        {CASTE_CATEGORIES.map(({ key, label }) => {
          const { payableAmount, isFree, discountPercentage } = getTuitionFeeDisplay(
            program,
            key,
            tuitionBase,
            yearStage,
          );
          const payablePct = 100 - discountPercentage;
          return (
            <div
              key={key}
              className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5 border border-amber-100"
            >
              <span className="font-medium text-gray-700">{label}</span>
              <span className="text-gray-600">
                {isFree ? (
                  <span className="text-green-600 font-semibold">
                    ₹0 (auto-waived)
                  </span>
                ) : (
                  <>
                    <span className="font-semibold text-gray-800">
                      {formatCurrency(payableAmount)}
                    </span>{" "}
                    <span className="text-gray-400">({payablePct}%)</span>
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </PreviewShell>
  );
};

export default FeeStructuresTab;
